import '../style/global.css';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { IconButton, Popover, Stack, TextField, Typography } from '@mui/material';
import Box from "@mui/material/Box";
import { DataGrid } from '@mui/x-data-grid';
import { GridColDef } from '@mui/x-data-grid/models/colDef';
import React from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { ActivityEnum, AdminSubactivityEnum, useUserActivityTracker } from '../UserActivityTracker';
import { ICommentReportRow, ReportApi } from '../api/ReportApi';
import background from '../asset/background-login.png';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';

// npm install @mui/x-data-grid --legacy-peer-deps
// npm install @mui/x-data-grid@5.13.1

export default function CommentReport() {

    useUserActivityTracker(ActivityEnum.Admin, AdminSubactivityEnum.CommentReport)

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();


    const [reportRowList, setReportRowList] = React.useState<ICommentReportRow[]>([]);
    const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });

    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

    const [emailFilter, setEmailFilter] = React.useState('');


    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedRowData, setSelectedRowData] = React.useState<any>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, row: any, index: number) => {
        setAnchorEl(event.currentTarget);
        setSelectedRowData(row);
        setSelectedIndex(index);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setSelectedRowData(null);
    };


    const columns: GridColDef[] = [
        { field: 'comment_id', headerName: 'Comment ID', width: 90 },
        { field: 'user_id', headerName: 'User ID', width: 90 },
        { field: 'user_first_name', headerName: 'First Name', width: 150 },
        { field: 'user_last_name', headerName: 'Last Name', width: 150 },
        { field: 'user_email', headerName: 'Email', width: 200 },
        { field: 'user_hsk_level', headerName: 'HSK', width: 90 },
        { field: 'good_comment', headerName: 'HSK', width: 90 },
        { field: 'bad_comment', headerName: 'HSK', width: 90 },
        { field: 'bug_report', headerName: 'HSK', width: 90 },
        {
            field: 'created_at',
            headerName: 'Timestamp',
            type: 'dateTime', // enables proper sorting and formatting
            width: 240,
            valueFormatter: (value: Date | string | null) =>
                value ? new Date(value).toLocaleString() : '',
        }

    ];

    React.useEffect(() => {
        ReportApi.getComment()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("Unable to CourseApi.getAll() " + response.status)
            })
            .then(list => {
                setReportRowList(list);
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [history, location, setErrorMessage]);



    const filteredRows = reportRowList
        .filter((row) =>
            emailFilter ? row.user_email.toLowerCase().includes(emailFilter.toLowerCase()) : true
        )
        .map((row) => ({
            ...row,
            id: `${row.user_id}-${row.comment_id}`,
            created_at: new Date(row.created_at), // Convert to Date object
        }));

    const open = Boolean(anchorEl);


    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!open || selectedIndex == null) return;

            const { page: currentPage, pageSize } = paginationModel;
            const totalPages = Math.ceil(filteredRows.length / pageSize);
            let newPage = currentPage;
            let newIndex = selectedIndex;

            if (event.key === 'ArrowUp') {
                newIndex = Math.max(0, selectedIndex - 1);
            } else if (event.key === 'ArrowDown') {
                newIndex = Math.min(filteredRows.length - 1, selectedIndex + 1);
            } else if (event.key === 'ArrowLeft') {
                if (currentPage > 0) {
                    newPage = currentPage - 1;
                    const startIndex = newPage * pageSize;
                    const endIndex = Math.min(startIndex + pageSize, filteredRows.length);
                    newIndex = endIndex - 1; // last row of previous page
                } else {
                    return;
                }
            } else if (event.key === 'ArrowRight') {
                if (currentPage < totalPages - 1) {
                    newPage = currentPage + 1;
                    newIndex = newPage * pageSize; // first row of next page
                } else {
                    return;
                }
            } else {
                return;
            }

            const newRow = filteredRows[newIndex];
            setSelectedIndex(newIndex);
            setPaginationModel((prev) => ({ ...prev, page: newPage }));

            setTimeout(() => {
                const updatedRowEl = document.querySelector(`[data-id="${newRow.id}"]`);
                if (updatedRowEl) {
                    setSelectedRowData(newRow);
                    setAnchorEl(updatedRowEl as HTMLElement);
                    updatedRowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);

            event.preventDefault();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, selectedIndex, filteredRows, paginationModel]);

    return (
        <div style={{
            display: 'flex', // Use flex to allow easy centering
            flexDirection: 'column', // Changed to stack children vertically
            justifyContent: 'center', // Center horizontally
            alignItems: 'center', // Center vertically
            // minHeight: 'calc(100vh - 85px)', // Ensure the div takes at least the full viewport height
            width: '100%', // Ensure the div takes the full viewport width
            padding: 0, // Reset any padding
            backgroundImage: `url(${background})`, // Your background image
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover', // Cover the entire size of the div
            backgroundPosition: 'center', // Center the background image
        }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '50%' }}>
                <TextField
                    label="Email"
                    value={emailFilter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailFilter(e.target.value)}
                    fullWidth
                />
            </Box>

            <Box
                sx={{
                    mt: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    // maxHeight: 'calc(100vh - 300px)', // Viewport height minus 100px
                    width: '90%', // Set the width of the Box
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    overflowY: 'auto', // Allows content to scroll if it overflows the Y axis
                    border: '1.5px solid rgba(82, 99, 125, 0.2)', // Added border with color and transparency
                    padding: 4,
                    position: 'relative', // Required for absolute children to anchor
                }}
            >
                <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    autoHeight
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    onRowClick={(params, event) => {
                        const index = filteredRows.findIndex(row => row.id === params.id);
                        handlePopoverOpen(event, params.row, index)

                    }}

                    getRowClassName={(params) =>
                        selectedRowData != null && selectedRowData.id === params.row.id ? 'Mui-selected-row' : ''
                    }
                    sx={{
                        width: '100%',
                        '& .MuiDataGrid-row': {
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#F0F4FF', // light blue or any hover color
                        },
                        '& .MuiDataGrid-cell': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        '& .Mui-selected-row': {
                            outline: '2px solid #0062FF',
                            backgroundColor: '#F0F4FF',
                        },
                    }}
                />
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                >
                    <Box sx={{ p: 2, maxWidth: '80vw' }}>
                        {selectedRowData && (
                            <Stack spacing={2}>
                                {/* Speaker Icon and Close Icon in same row */}
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>↑</span>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>↓</span>
                                        </span>
                                        navigate rows
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>←</span>
                                            <span style={{ border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px', fontSize: '0.85rem' }}>→</span>
                                        </span>
                                        change page
                                    </Typography>

                                    <IconButton onClick={handlePopoverClose}>
                                        <CloseRoundedIcon />
                                    </IconButton>
                                </Stack>
                                {/* Display content */}
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2">HSK Level: {selectedRowData.user_hsk_level && '—'}</Typography>
                                    <Typography variant="body1"><strong>Positive:</strong> {selectedRowData.good_comment || '—'}</Typography>
                                    <Typography variant="body1"><strong>Negative:</strong> {selectedRowData.bad_comment || '—'}</Typography>
                                    <Typography variant="body1"><strong>Bug Report:</strong> {selectedRowData.bug_report || '—'}</Typography>
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                </Popover>
            </Box>
        </div>
    );
}